// @flow

import * as React from 'react'
import moment from 'moment'
import linkViewModel from '@vimcar/shared-utils/linkViewModel'
import CircularProgress from 'material-ui/CircularProgress'
import Card from '@vimcar/shared-components/components/Card'
import InventoryCalendar from '@vimcar/shared-components/components/InventoryCalendar'
import SwaggerForm from '@vimcar/shared-components/components/SwaggerForm'
import BookingTimeframeSelector from '@vimcar/shared-components/components/BookingTimeframeSelector'
import Dialog from '@vimcar/shared-components/components/Dialog'
import DialogActions from '@vimcar/shared-components/components/DialogActions'

import i18n from '#utils/i18n'
import BookingDialog from '#components/BookingDialog'
import Err from '#components/Err'
import WalkthroughWrapper from '#components/WalkthroughWrapper'
import VehicleColumnLabel from '#components/VehicleColumnLabel'
import Warning from '#components/Warning'
import colors from '#styles/colors'
import AvailableVehiclesList from './components/AvailableVehiclesList'
import mkViewModel, { type ViewModel } from './viewModel'
import styles from './styles.css'
import definitions from './definitions'

const tp = i18n.prefix('components.CreateBooking')

type Props = {
  viewModel: ViewModel,
}

const CreateBooking = ({ viewModel }: Props) => (
  <div
    className={styles.container}
    onKeyDown={viewModel.handleKeyDown}
    tabIndex={0}
    >
    <Card className={styles.timeframe}>
      <BookingTimeframeSelector
        className={styles.timeframeSelector}
        disabled={viewModel.checkClickedOnce}
        end={viewModel.end}
        onChange={viewModel.handleChangeTimeframe}
        onCheck={viewModel.handleClickCheck}
        // $FlowFixMe flow does not seem to know about React.createRef
        ref={viewModel.bookingTimeFrameSelectorRef}
        start={viewModel.start}
        timezone={viewModel.timezone}
        />
    </Card>

    {viewModel.showNoTimeframeWarning && (
      <Warning
        text={tp('no-timeframe-text')}
        title={tp('no-timeframe-title')}
        />
    )}

    {viewModel.error && <Err>{viewModel.error}</Err>}

    {viewModel.showSimplifiedView && (
      <div>
        {viewModel.noVehiclesAvailable && (
          <React.Fragment>
            <Warning
              color={colors.colorPrivate}
              text={tp('no-results-text')}
              title={tp('no-results-title')}
              />
            <div className={styles.contextControls} key='contextControls'>
              <button
                className={styles.contextButton}
                onClick={viewModel.handleClickShowCalendar}
                >
                {tp('show-calendar')}
              </button>
            </div>
          </React.Fragment>
        )}

        {viewModel.showLoader && (
          <div className={styles.message}>
            <CircularProgress/>
          </div>
        )}

        {viewModel.vehiclesAvailable && (
          <div className={styles.availableVehiclesList}>
            <AvailableVehiclesList
              availableVehicles={viewModel.availableVehicles}
              className={styles.availableVehiclesList}
              disableAction={viewModel.createBookingLoading}
              key='vehicles'
              />
            <div className={styles.contextControls} key='contextControls'>
              <button
                className={styles.contextButton}
                onClick={viewModel.handleClickShowCalendar}
                >
                {tp('show-calendar')}
              </button>
            </div>
            <WalkthroughWrapper
              onCallback={viewModel.handleSimpleViewCloseBookingLayover}
              run={viewModel.showSimpleViewBookingLayover}
              type='single'
              walkThroughSteps={viewModel.simpleViewBookingLayoverSteps}
              />
          </div>
        )}

        <WalkthroughWrapper
          onCallback={viewModel.handleSimpleViewCloseTimeFrameLayover}
          run={viewModel.showSimpleViewTimeFrameLayover}
          type='single'
          walkThroughSteps={viewModel.simpleViewTimeFrameLayoverSteps}
          />
      </div>
    )}

    {viewModel.showCalendarView && (
      <div className={styles.walkthrough}>
        <InventoryCalendar
          enableDetailViews
          showCurrentTime
          center={+moment()}
          className={styles.calendar}
          entries={viewModel.entries}
          localization={viewModel.localization}
          onCreateEntry={viewModel.handleCreateEntry}
          ref={viewModel.setInventoryCalendarRef}
          resources={viewModel.resources}
          setTimeframe={viewModel.getTimeframe}
          submitLabel={tp('book')}
          timezone={viewModel.timezone}
          transformColumnLabel={VehicleColumnLabel}
          unit='hour'
          />
        <WalkthroughWrapper
          onCallback={viewModel.handleCalendarViewCloseTimeFrameLayover}
          run={viewModel.showCalendarViewTimeFrameLayover}
          type='single'
          walkThroughSteps={viewModel.calendarViewTimeFrameLayoverSteps}
          />
        <WalkthroughWrapper
          showStepsProgress
          onCallback={viewModel.handleCalendarViewBookingLayover}
          run={viewModel.showCalendarViewBookingLayover}
          type='continuous'
          walkThroughSteps={viewModel.calendarViewBookingLayoverSteps}
          />
      </div>
    )}

    {viewModel.showConfirmDialog && viewModel.newBooking && (
      <BookingDialog
        definitions={definitions.bookingDetailsRequired}
        end={viewModel.newBooking.formattedEnd}
        fields={viewModel.bookingDetailsRequiredFields}
        formRef={viewModel.setBookingDialogFormRef}
        licenseplate={viewModel.newBooking.licenseplate}
        loading={viewModel.createBookingLoading}
        makeModel={viewModel.newBooking.makeModel}
        onChange={viewModel.handleBookingDialogChange}
        onPrimary={viewModel.handleClickConfirm}
        onSecondary={viewModel.handleClickCancel}
        start={viewModel.newBooking.formattedStart}
        title={tp('create-booking')}
        >
        {viewModel.showBookingDetailsRequired && (
          <span>
            <SwaggerForm
              className={styles.form}
              definition={definitions.bookingDetailsRequired}
              fieldProps={{
                className: styles.field,
              }}
              fields={viewModel.bookingDetailsRequiredFields}
              i18n={i18n}
              onChange={viewModel.handleBookingDialogChange}
              ref={viewModel.setBookingDialogFormRef}
              />
            <p className={styles.description}>
              {tp('bookingDetailsRequired-description')}
            </p>
            {viewModel.formInvalidError && (
              <Err>{tp('bookingDetailsRequired-error')}</Err>
            )}
          </span>
        )}
        {viewModel.showCommentOnly && (
          <span>
            <SwaggerForm
              className={styles.form}
              definition={definitions.commentOnly}
              fields={viewModel.commentOnlyFields}
              i18n={i18n}
              onChange={viewModel.handleBookingDialogChange}
              ref={viewModel.setBookingDialogFormRef}
              />
            <p className={styles.description}>{tp('commentOnly-description')}</p>
          </span>
        )}
      </BookingDialog>
    )}
    {viewModel.showDoneDialog && (
      <Dialog
        footer={() => (
          <DialogActions
            onPrimary={viewModel.handleClickMyBookings}
            onSecondary={viewModel.handleClickNewBooking}
            primaryLabel={tp('my-bookings')}
            secondaryLabel={tp('new-booking')}
            />
        )}
        onClose={viewModel.handleClickNewBooking}
        title={viewModel.approvalNeeded
          ? tp('approval-needed-title')
          : tp('done-title')
        }
        >
        <p>
          {viewModel.approvalNeeded
            ? tp('approval-needed-text')
            : tp('done-text')
          }
        </p>
      </Dialog>
    )}
  </div>
)

export default linkViewModel<any, any>(mkViewModel, CreateBooking)
